import { LoadingButton } from "@mui/lab";
import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { TYPE_NOTIFICATION } from "../../../components/reusable/NotificationBull";
import useToast from "../../../hooks/useToast";
import { evaluation_refonte_reponse_service } from "../../../services/PersonalEvaluationRefonteService";

const REQUEST_STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  EMPTY: "empty",
  ERROR: "error",
};

const CommentaireConsultantEditForm = ({ open = true, onFormClose }) => {
  const [status, setStatus] = useState(REQUEST_STATUS.IDLE);
  const [interviewState, setInterviewState] = useState(null);
  const [comment, setComment] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const loadRequestIdRef = useRef(0);
  const { showToast, Toast } = useToast();

  const loadInterviewComment = useCallback(async (signal) => {
    const requestId = loadRequestIdRef.current + 1;
    setStatus(REQUEST_STATUS.LOADING);
    loadRequestIdRef.current = requestId;
    setErrorMessage("");

    try {
      const state =
        await evaluation_refonte_reponse_service.getInterviewCommentState(
          signal,
        );
      if (signal?.aborted || requestId !== loadRequestIdRef.current) return;

      setInterviewState(state);
      setComment(state.comment?.isFallback ? "" : state.comment?.value || "");
      setStatus(
        state.comment?.value || state.comment?.targetResponseExists
          ? REQUEST_STATUS.SUCCESS
          : REQUEST_STATUS.EMPTY,
      );
    } catch (error) {
      if (signal?.aborted || requestId !== loadRequestIdRef.current) return;
      setErrorMessage(error.message);
      setStatus(REQUEST_STATUS.ERROR);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      loadRequestIdRef.current += 1;
      setStatus(REQUEST_STATUS.IDLE);
      return undefined;
    }

    const controller = new AbortController();
    loadInterviewComment(controller.signal);
    return () => controller.abort();
  }, [loadInterviewComment, open]);

  const handleSave = async () => {
    const normalizedComment = comment.trim();
    if (!normalizedComment) {
      setErrorMessage("Le commentaire est obligatoire.");
      return;
    }

    setIsSaving(true);
    setErrorMessage("");
    try {
      const response =
        await evaluation_refonte_reponse_service.updateInterviewComment(
          normalizedComment,
        );
      setInterviewState(response.data);
      showToast(TYPE_NOTIFICATION.SUCCESS, response.message);
      onFormClose(true);
    } catch (error) {
      setErrorMessage(error.message);
      if (error.errorCode === "FINAL_EVALUATION_COMPLETED") {
        setInterviewState((previousState) =>
          previousState
            ? {
                ...previousState,
                canModify: false,
                isFinalEvaluationCompleted: true,
              }
            : previousState,
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  const isReadOnly = interviewState?.isFinalEvaluationCompleted;
  const hasTargetResponse =
    interviewState?.comment?.targetResponseExists !== false;
  const isSaveDisabled =
    isSaving ||
    isReadOnly ||
    !hasTargetResponse ||
    status === REQUEST_STATUS.LOADING ||
    status === REQUEST_STATUS.ERROR;

  return (
    <>
      <Toast />
      <Dialog
        open={open}
        onClose={() => {
          if (!isSaving) {
            onFormClose(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Modifier le commentaire du consultant sur l’entrevue
        </DialogTitle>
        <DialogContent>
          {status === REQUEST_STATUS.LOADING && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {status === REQUEST_STATUS.ERROR && (
            <Alert
              severity="error"
              action={
                <LoadingButton onClick={() => loadInterviewComment()}>
                  Réessayer
                </LoadingButton>
              }
            >
              {errorMessage || "Impossible de charger le commentaire."}
            </Alert>
          )}

          {(status === REQUEST_STATUS.SUCCESS ||
            status === REQUEST_STATUS.EMPTY) && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              {interviewState?.currentEvaluator && (
                <Typography variant="body2" color="text.secondary">
                  Entretien avec : {interviewState.currentEvaluator.name}
                </Typography>
              )}

              {isReadOnly && (
                <Alert severity="info">
                  Le circuit d’évaluation est terminé. Le commentaire ne peut
                  plus être modifié.
                </Alert>
              )}

              {interviewState?.comment?.isFallback && (
                <Alert severity="info">
                  <AlertTitle>Dernier commentaire disponible</AlertTitle>
                  Entretien avec{" "}
                  {interviewState.comment.sourceEvaluatorName || "évaluateur"}
                  <Typography sx={{ whiteSpace: "pre-wrap", mt: 1 }}>
                    {interviewState.comment.value}
                  </Typography>
                </Alert>
              )}

              {!hasTargetResponse && (
                <Alert severity="warning">
                  Aucun entretien enregistré ne permet encore de créer un
                  commentaire pour cet évaluateur.
                </Alert>
              )}

              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={12}
                label="Commentaire"
                required
                disabled={isReadOnly || !hasTargetResponse}
                error={Boolean(errorMessage)}
                helperText={errorMessage}
                value={comment}
                onChange={(event) => {
                  setErrorMessage("");
                  setComment(event.target.value);
                }}
                sx={{
                  "& textarea": {
                    resize: "vertical",
                  },
                }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <LoadingButton
            onClick={handleSave}
            loading={isSaving}
            disabled={isSaveDisabled}
          >
            Sauvegarder
          </LoadingButton>
          <LoadingButton onClick={() => onFormClose(false)} disabled={isSaving}>
            Fermer
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CommentaireConsultantEditForm;
